/**
 * CustomButtons Component
 *
 * Renders custom buttons configured in Object Manager for detail pages.
 * Supports header, section, and footer placements with visibility rules.
 *
 * @module components/common/CustomButtons
 */

import React, { useState } from 'react';
import { useButtonActions, ButtonAction, ButtonLocation, ModalType } from '../../services/queries/buttonActionQueries';
import {
  getButtonsForLocation,
  getDetailPageButtons,
  executeButtonAction,
  getButtonColorClasses,
  getButtonOutlineClasses,
  ButtonWithIcon
} from '../../services/customButtonService';
import { useToast } from '../../contexts/ToastContext';
import { Loader2 } from 'lucide-react';

// ============================================
// TYPES
// ============================================

interface CustomButtonsProps {
  /** The API name of the object (e.g., 'Client__c') */
  objectName: string;
  /** Where to render buttons */
  location: ButtonLocation;
  /** Current record data for visibility evaluation and action execution */
  recordData: Record<string, any>;
  /** Record ID for action execution */
  recordId: string;
  /** Callback when fields should be updated */
  onUpdateFields?: (updates: Record<string, any>) => Promise<void>;
  /** Callback for navigation */
  onNavigate?: (url: string, newTab: boolean) => void;
  /** Callback to open a modal (e.g., send-email, log-call) */
  onOpenModal?: (modalType: ModalType, recordData: Record<string, any>) => void;
  /** Use outline style instead of filled */
  outline?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Button size */
  size?: 'sm' | 'md' | 'lg';
  /** Show loading state */
  showLoading?: boolean;
}

interface DetailPageButtonsProps {
  /** The API name of the object (e.g., 'Client__c') */
  objectName: string;
  /** Current record data for visibility evaluation */
  recordData: Record<string, any>;
  /** Record ID for action execution */
  recordId: string;
  /** Callback when fields should be updated */
  onUpdateFields?: (updates: Record<string, any>) => Promise<void>;
  /** Callback for navigation */
  onNavigate?: (url: string, newTab: boolean) => void;
  /** Callback to open a modal (e.g., send-email, log-call) */
  onOpenModal?: (modalType: ModalType, recordData: Record<string, any>) => void;
}

// ============================================
// SINGLE BUTTON COMPONENT
// ============================================

interface CustomButtonProps {
  button: ButtonWithIcon;
  recordData: Record<string, any>;
  recordId: string;
  onUpdateFields?: (updates: Record<string, any>) => Promise<void>;
  onNavigate?: (url: string, newTab: boolean) => void;
  onOpenModal?: (modalType: ModalType, recordData: Record<string, any>) => void;
  outline?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

const CustomButton: React.FC<CustomButtonProps> = ({
  button,
  recordData,
  recordId,
  onUpdateFields,
  onNavigate,
  onOpenModal,
  outline = false,
  size = 'md'
}) => {
  const [isExecuting, setIsExecuting] = useState(false);
  const { success, error: showError } = useToast();

  const handleClick = async () => {
    setIsExecuting(true);

    try {
      const result = await executeButtonAction(
        button,
        recordData,
        recordId,
        {
          onUpdateFields,
          onNavigate: onNavigate || ((url, newTab) => {
            if (newTab) {
              window.open(url, '_blank');
            } else {
              window.location.href = url;
            }
          }),
          onShowToast: (type, message) => {
            if (type === 'success') {
              success('Action Complete', message);
            } else {
              showError('Action Failed', message);
            }
          },
          onOpenModal
        }
      );

      // Don't show success toast for open-modal actions - the modal handles its own toast
      if (result.success && button.action.type !== 'open-modal') {
        success(button.label, button.successMessage || result.message);
      } else if (!result.success) {
        showError(button.label, button.errorMessage || result.message);
      }
    } catch (err) {
      showError(button.label, button.errorMessage || 'An unexpected error occurred');
    } finally {
      setIsExecuting(false);
    }
  };

  // Size classes
  const sizeClasses = {
    sm: 'px-2 py-1 text-xs',
    md: 'px-3 py-1.5 text-sm',
    lg: 'px-4 py-2 text-base'
  };

  const IconComponent = button.IconComponent;

  const baseClasses = `
    inline-flex items-center gap-1.5 font-medium rounded-lg
    transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed
    focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
    ${sizeClasses[size]}
  `;

  const colorClasses = outline
    ? `border ${getButtonOutlineClasses(button.color)}`
    : getButtonColorClasses(button.color);

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isExecuting}
      className={`${baseClasses} ${colorClasses}`}
      title={button.name}
    >
      {isExecuting ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : IconComponent ? (
        <IconComponent className="w-4 h-4" />
      ) : null}
      <span>{button.label}</span>
    </button>
  );
};

// ============================================
// CUSTOM BUTTONS GROUP COMPONENT
// ============================================

export const CustomButtons: React.FC<CustomButtonsProps> = ({
  objectName,
  location,
  recordData,
  recordId,
  onUpdateFields,
  onNavigate,
  onOpenModal,
  outline = false,
  className = '',
  size = 'md',
  showLoading = true
}) => {
  const { data: allButtons, isLoading } = useButtonActions(objectName, { isActive: true });

  if (isLoading && showLoading) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <div className="animate-pulse flex gap-2">
          <div className="h-8 w-20 bg-gray-200 dark:bg-gray-700 rounded-lg" />
          <div className="h-8 w-24 bg-gray-200 dark:bg-gray-700 rounded-lg" />
        </div>
      </div>
    );
  }

  if (!allButtons || allButtons.length === 0) {
    return null;
  }

  const buttons = getButtonsForLocation(allButtons, objectName, location, recordData);

  if (buttons.length === 0) {
    return null;
  }

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      {buttons.map(button => (
        <CustomButton
          key={button.id}
          button={button}
          recordData={recordData}
          recordId={recordId}
          onUpdateFields={onUpdateFields}
          onNavigate={onNavigate}
          onOpenModal={onOpenModal}
          outline={outline}
          size={size}
        />
      ))}
    </div>
  );
};

// ============================================
// DETAIL PAGE BUTTONS COMPONENT
// ============================================

/**
 * Renders all custom buttons for a detail page (header, section, footer)
 * Use this for quick integration into detail pages
 */
export const DetailPageButtons: React.FC<DetailPageButtonsProps> = ({
  objectName,
  recordData,
  recordId,
  onUpdateFields,
  onNavigate,
  onOpenModal
}) => {
  const { data: allButtons, isLoading } = useButtonActions(objectName, { isActive: true });

  if (isLoading || !allButtons || allButtons.length === 0) {
    return null;
  }

  const { header, section, footer } = getDetailPageButtons(allButtons, objectName, recordData);

  // Check if any buttons exist for this object
  const hasAnyButtons = header.length > 0 || section.length > 0 || footer.length > 0;

  if (!hasAnyButtons) {
    return null;
  }

  return (
    <>
      {/* Header buttons are rendered inline with the page header */}
      {header.length > 0 && (
        <CustomButtons
          objectName={objectName}
          location="detail-header"
          recordData={recordData}
          recordId={recordId}
          onUpdateFields={onUpdateFields}
          onNavigate={onNavigate}
          onOpenModal={onOpenModal}
          showLoading={false}
        />
      )}
    </>
  );
};

// ============================================
// HEADER BUTTONS COMPONENT
// ============================================

interface HeaderButtonsProps {
  objectName: string;
  recordData: Record<string, any>;
  recordId: string;
  onUpdateFields?: (updates: Record<string, any>) => Promise<void>;
  onNavigate?: (url: string, newTab: boolean) => void;
  onOpenModal?: (modalType: ModalType, recordData: Record<string, any>) => void;
  className?: string;
}

/**
 * Custom buttons specifically for detail page headers
 */
export const HeaderButtons: React.FC<HeaderButtonsProps> = ({
  objectName,
  recordData,
  recordId,
  onUpdateFields,
  onNavigate,
  onOpenModal,
  className = ''
}) => {
  return (
    <CustomButtons
      objectName={objectName}
      location="detail-header"
      recordData={recordData}
      recordId={recordId}
      onUpdateFields={onUpdateFields}
      onNavigate={onNavigate}
      onOpenModal={onOpenModal}
      className={className}
      showLoading={false}
    />
  );
};

// ============================================
// SECTION BUTTONS COMPONENT
// ============================================

interface SectionButtonsProps {
  objectName: string;
  recordData: Record<string, any>;
  recordId: string;
  onUpdateFields?: (updates: Record<string, any>) => Promise<void>;
  onNavigate?: (url: string, newTab: boolean) => void;
  onOpenModal?: (modalType: ModalType, recordData: Record<string, any>) => void;
  title?: string;
  className?: string;
}

/**
 * Custom buttons for detail page sections with optional title
 */
export const SectionButtons: React.FC<SectionButtonsProps> = ({
  objectName,
  recordData,
  recordId,
  onUpdateFields,
  onNavigate,
  onOpenModal,
  title = 'Quick Actions',
  className = ''
}) => {
  const { data: allButtons } = useButtonActions(objectName, { isActive: true });

  if (!allButtons) return null;

  const buttons = getButtonsForLocation(allButtons, objectName, 'detail-section', recordData);

  if (buttons.length === 0) return null;

  return (
    <div className={`bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-4 ${className}`}>
      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
        {title}
      </h3>
      <CustomButtons
        objectName={objectName}
        location="detail-section"
        recordData={recordData}
        recordId={recordId}
        onUpdateFields={onUpdateFields}
        onNavigate={onNavigate}
        onOpenModal={onOpenModal}
        outline
        showLoading={false}
      />
    </div>
  );
};

// ============================================
// FOOTER BUTTONS COMPONENT
// ============================================

interface FooterButtonsProps {
  objectName: string;
  recordData: Record<string, any>;
  recordId: string;
  onUpdateFields?: (updates: Record<string, any>) => Promise<void>;
  onNavigate?: (url: string, newTab: boolean) => void;
  onOpenModal?: (modalType: ModalType, recordData: Record<string, any>) => void;
  className?: string;
}

/**
 * Custom buttons for detail page footers
 */
export const FooterButtons: React.FC<FooterButtonsProps> = ({
  objectName,
  recordData,
  recordId,
  onUpdateFields,
  onNavigate,
  onOpenModal,
  className = ''
}) => {
  return (
    <div className={`border-t border-gray-200 dark:border-gray-700 pt-4 ${className}`}>
      <CustomButtons
        objectName={objectName}
        location="detail-footer"
        recordData={recordData}
        recordId={recordId}
        onUpdateFields={onUpdateFields}
        onNavigate={onNavigate}
        onOpenModal={onOpenModal}
        size="lg"
        showLoading={false}
      />
    </div>
  );
};

// ============================================
// LIST BUTTONS COMPONENT
// ============================================

interface ListRowButtonsProps {
  objectName: string;
  recordData: Record<string, any>;
  recordId: string;
  onUpdateFields?: (updates: Record<string, any>) => Promise<void>;
  onOpenModal?: (modalType: ModalType, recordData: Record<string, any>) => void;
  className?: string;
}

/**
 * Custom buttons for list view row actions
 */
export const ListRowButtons: React.FC<ListRowButtonsProps> = ({
  objectName,
  recordData,
  recordId,
  onUpdateFields,
  onOpenModal,
  className = ''
}) => {
  return (
    <CustomButtons
      objectName={objectName}
      location="list-row"
      recordData={recordData}
      recordId={recordId}
      onUpdateFields={onUpdateFields}
      onOpenModal={onOpenModal}
      size="sm"
      className={className}
      showLoading={false}
    />
  );
};

export default CustomButtons;
